export interface ProductDetails {
  code: string;
  sku: string;
  details: Record<string, unknown>;
  images: string[];
  cod_stagione?: number | string;
}

export const NO_IMAGE_PLACEHOLDER = `data:image/svg+xml;utf8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="220" height="220" viewBox="0 0 220 220">
  <rect width="220" height="220" fill="#f3f0eb"/>
  <rect x="18" y="18" width="184" height="184" fill="none" stroke="#b8aea3" stroke-width="2" stroke-dasharray="8 6"/>
  <circle cx="82" cy="84" r="16" fill="#d6cec5"/>
  <path d="M48 162l42-44 26 28 18-18 38 34H48z" fill="#d6cec5"/>
  <text x="110" y="190" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="22" font-weight="700" fill="#6f665d">NO IMAGE</text>
</svg>
`)}`;

const IGNORED_DETAIL_KEYS = new Set([
  'sku',
  'cod_stagione',
  'cod_modello',
  'cod_colore',
  'sketch_url',
  'cod_immagine',
  'codice_fornitori',
  'codice_tessuto',
  'bgadescriz1',
  'url_img_list',
  'bgcgrmtqd',
  'bgcgrmtln',
  'consegna',
  'fascia_prezzo',
]);

export function buildImage(imageId: string | null | undefined): string | null {
  if (!imageId) {
    return null;
  }
  return `https://media.brunellocucinelli.com/image/upload/ar_1:1,c_fill,w_220,g_north/${imageId}`;
}

export function normaliseProductDetails(raw: Record<string, unknown> | null | undefined): ProductDetails {
  const data = raw ?? {};
  const codeParts = [
    data['cod_stagione'],
    data['cod_modello'],
    data['cod_colore'],
  ]
    .map(value => (typeof value === 'string' ? value.trim() : value?.toString().trim() ?? ''))
    .filter(Boolean);
  const code = codeParts.join(' ');
  const sku = typeof data['sku'] === 'string' ? data['sku'] : '';

  const detailEntries = Object.entries(data)
    .filter(([key, value]) => !IGNORED_DETAIL_KEYS.has(key) && value !== null && value !== undefined)
    .reduce<Record<string, unknown>>((acc, [key, value]) => {
      acc[key] = value;
      return acc;
    }, {});

  const urlImgList = Array.isArray(data['url_img_list'])
    ? (data['url_img_list'] as unknown[])
    : typeof data['url_img_list'] === 'string'
      ? (data['url_img_list'] as string)
          .replace(/[{}]/g, '')
          .split(',')
          .map(item => item.trim())
      : [];

  const rawImages: string[] = [];
  for (const entry of urlImgList) {
    if (typeof entry === 'string' && entry.trim().length > 0) {
      rawImages.push(entry.trim());
    }
  }
  if (typeof data['sketch_url'] === 'string' && data['sketch_url'].trim()) {
    rawImages.push(data['sketch_url'].trim());
  }

  const images = rawImages
    .map(id => buildImage(id))
    .filter((value): value is string => Boolean(value));
  const imagesWithFallback = images.length > 0 ? images : [NO_IMAGE_PLACEHOLDER];

  const codStagione = data['cod_stagione'];
  const parsedCodStagione = typeof codStagione === 'number' 
    ? codStagione 
    : typeof codStagione === 'string' 
      ? parseInt(codStagione, 10) 
      : undefined;

  return {
    code,
    sku,
    details: detailEntries,
    images: imagesWithFallback,
    cod_stagione: !isNaN(parsedCodStagione as number) ? parsedCodStagione : undefined,
  };
}
